import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { APIError, client } from "@/api"
import { sortUsersForExport, type ExportableUser } from "@/lib/userExport"

import type { WelcomeOfferStage } from "./useWelcomeOffers"

export type UsersSort = "created_at" | "last_seen"

// The four states an admin can move a claimed coach between (accepted -> emailed ->
// onboarded, or cold). See docs/exec-plans/active/diagrams-welcome-offer.md (2026-09-24).
export type AdminWelcomeOfferStatus = "accepted" | "emailed" | "onboarded" | "cold"

// Full DB-stored status set, including the coach-facing shown/dismissed.
export type WelcomeOfferStatus = "shown" | "dismissed" | AdminWelcomeOfferStatus

// Legacy directory filters remain valid for bookmarked Users URLs; claimed includes cold.
export type WelcomeOfferListFilter = "claimed" | "details_received" | AdminWelcomeOfferStatus

interface UsersQueryParams {
  limit?: number
  offset?: number
  search?: string
  sort?: UsersSort
  welcomeOffer?: WelcomeOfferListFilter
}

export interface AdminUserTeam {
  public_id: string
  name: string
  role: string
}

// API response type that matches the backend admin endpoint
export interface AdminUser {
  public_id: string
  name: string
  email: string
  email_verified: boolean
  avatar: string | null
  is_superuser: boolean
  suspended_at: string | null
  suspension_reason: string | null
  suspension_expires_at: string | null
  token_version: number
  last_login: string | null
  last_seen_at: string | null
  individual_storage_used_bytes: number
  diagram_count: number
  welcome_offer_submitted_at?: string | null
  welcome_offer_stage: WelcomeOfferStage | null
  welcome_offer_status: WelcomeOfferStatus | null
  created_at: string
  teams: AdminUserTeam[]
  signup_surface?: string | null
  signup_source?: {
    referrer_host?: string
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    landing?: string
  } | null
  /** Whether a Google Ads click id is stored. The id itself is never sent. */
  has_google_ads_click?: boolean
}

export interface AdminWelcomeOfferDetail {
  stage: WelcomeOfferStage
  setup_team_name?: string | null
  setup_level?: string | null
  setup_league?: string | null
  setup_submitted_at?: string | null
  setup_declined_at?: string | null
  setup_email_sent_at?: string | null
  setup_reminder_sent_at?: string | null
  setup_email_due_at?: string | null
  setup_email_attempts?: number

  offer_key: string
  status: WelcomeOfferStatus
  shown_at: string
  responded_at: string | null
  updated_at: string
}

interface UsersResponse {
  users: AdminUser[]
  total?: number
  /** Set only when a welcome-offer filter is on. */
  welcomeOfferSummary: { claimed_count: number; cap: number } | null
}

export function useUsers({
  limit = 20,
  offset = 0,
  search = "",
  sort = "created_at",
  welcomeOffer,
}: UsersQueryParams = {}) {
  return useQuery<UsersResponse>({
    queryKey: ["admin", "users", { limit, offset, search, sort, welcomeOffer }],
    queryFn: async () => {
      const query: Record<string, string> = {
        limit: limit.toString(),
        offset: offset.toString(),
        sort,
      }
      if (search) query.search = search
      if (welcomeOffer) query.welcome_offer = welcomeOffer
      const response = await (client).admin.users.$get({ query })

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`)
      }

      const data = await response.json()
      // Backend returns object with users array (no total field)
      return { 
        users: data.users || [],
        total: data.users?.length || 0,
        welcomeOfferSummary: data.welcome_offer_summary ?? null,
      }
    },
  })
}

/**
 * Request one complete, audited export from the backend. The server performs
 * bounded keyset paging internally and returns only the user-table fields used
 * by the CSV. Search is intentionally omitted: Export means all users.
 */
export async function getAllUsersForExport(sort: UsersSort): Promise<ExportableUser[]> {
  const response = await client.admin.users.export.$post()
  if (!response.ok) {
    throw new Error(`Failed to export users: ${response.status}`)
  }

  const data = await response.json()
  return sortUsersForExport(data.users as ExportableUser[], sort)
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      const response = await client.admin.users[":publicId"].$get({
        param: { publicId: userId },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch user: ${response.status}`)
      }

      return response.json()
    },
    enabled: !!userId,
  })
}

export interface UpdateWelcomeOfferStatusResponse {
  welcome_offer: AdminWelcomeOfferDetail
  claimed_count: number
  cap: number
}

/**
 * Move a coach who has already claimed a welcome-offer spot between the admin follow-up
 * states (accepted -> emailed -> onboarded, or cold). See
 * docs/exec-plans/active/diagrams-welcome-offer.md (2026-09-24). Invalidates both the user
 * detail (`useUserDetail`'s ["user", userId] key) and the users list, since the list's Offer
 * column and the "N of 15 claimed" count both depend on this.
 */
export function useUpdateWelcomeOfferStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateWelcomeOfferStatusResponse,
    APIError,
    { userId: string; status: AdminWelcomeOfferStatus }
  >({
    mutationFn: async ({ userId, status }) => {
      const response = await (
        client.admin.users[":publicId"]["welcome-offer"] as any
      ).$put({
        param: { publicId: userId },
        json: { status },
      })

      if (!response.ok) {
        const err = (await response.clone().json().catch(() => ({}))) as {
          message?: string
          error?: string
        }
        throw new APIError(
          response.status,
          "UPDATE_WELCOME_OFFER_STATUS_FAILED",
          new Error(err.message || err.error || "Failed to update welcome-offer status"),
          err
        )
      }

      return response.json()
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] })
      queryClient.invalidateQueries({ queryKey: ["admin", "welcome-offers"] })
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}

// Human-readable translations for the deletion blockers the backend can return
// (409). Keep in sync with backend/src/lib/account-deletion/blockers.ts.
const DELETION_BLOCKER_LABELS: Record<string, string> = {
  sole_team_admin:
    "This user is the sole admin of a team — reassign or add another team admin first.",
}

export function describeDeletionBlocker(blocker: string): string {
  return DELETION_BLOCKER_LABELS[blocker] ?? blocker
}

/**
 * Turn a failed delete into a user-facing message.
 *
 * The shared API client (api/index.ts) throws on any non-2xx and attaches the
 * parsed JSON body. Different statuses use different shapes:
 *  - 403 / 404 -> { message }            (HTTPException)
 *  - 409       -> { error, blockers }     (can't delete yet)
 *  - 501       -> { error, remaining }    (deletion not fully supported)
 * Prefer the most specific, human-readable line we can build.
 */
export function describeDeleteUserError(error: unknown): string {
  if (error instanceof APIError) {
    const body = error.body as
      | { message?: string; error?: string; blockers?: string[]; remaining?: string[] }
      | undefined

    // 409: refused with hard blockers — translate them to human lines.
    if (body?.blockers && body.blockers.length > 0) {
      const lines = body.blockers.map(describeDeletionBlocker).join(" ")
      return body.error ? `${body.error} ${lines}` : lines
    }

    // 409/501 use `error`; 403/404 use `message`.
    return (
      body?.error ||
      body?.message ||
      error.originalError.message ||
      "Failed to delete user. Please try again."
    )
  }

  return error instanceof Error
    ? error.message
    : "Failed to delete user. Please try again."
}

/**
 * Staff-initiated account deletion.
 * DELETE /admin/users/:publicId with body { confirm: "DELETE" }.
 * 200 -> deleted; non-2xx throws an APIError (see describeDeleteUserError).
 * Mirrors the other admin user mutations: same client + list invalidation.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation<unknown, APIError, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const response = await (client.admin.users[":publicId"] as any).$delete({
        param: { publicId: userId },
        json: { confirm: "DELETE" },
      })

      // Defensive: the shared client throws on non-2xx before we get here, but
      // keep the guard so this hook is correct even if that wrapper changes.
      if (!response.ok) {
        const err = (await response.clone().json().catch(() => ({}))) as {
          message?: string
          error?: string
          code?: string
        }
        throw new APIError(
          response.status,
          err.code || "DELETE_USER_FAILED",
          new Error(err.message || err.error || "Failed to delete user"),
          err
        )
      }

      return response.json()
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "welcome-offers"] })
      // Invalidate the users list (prefix-matches every paginated/search key)
      // and drop the now-deleted user's detail cache.
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] })
      queryClient.removeQueries({ queryKey: ["user", variables.userId] })
    },
  })
}
